import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Hydrates KM Recently Viewed carousel from localStorage + Section Rendering (/search).
 */
class KmRecentlyViewedLoader extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  disconnectedCallback() {
    this.#abort?.abort();
  }

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.#hydrate();
  }

  async #hydrate() {
    const ac = this.#abort;
    if (!ac) return;
    const root = this.closest('.shopify-section');
    const track = root?.querySelector('[data-km-bestsellers-track]');
    const slidesSection = this.dataset.slidesSection;
    const searchUrl = this.dataset.searchUrl;
    const excludeId = this.dataset.excludeProductId || '';
    const max = Number(this.dataset.maxProducts || '8');

    if (!root || !track || !slidesSection || !searchUrl) return;

    let ids = RecentlyViewed.getProducts().filter((id) => String(id) !== String(excludeId));
    ids = ids.slice(0, max);
    if (ids.length === 0) {
      root.hidden = true;
      return;
    }

    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', ids.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', slidesSection);

    let html;
    try {
      const res = await fetch(url.toString(), { signal: ac.signal });
      if (!res.ok) throw new Error(String(res.status));
      html = await res.text();
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.warn('[km-recently-viewed]', e);
      root.hidden = true;
      return;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const frag = doc.querySelector('.km-rv-frag');
    if (!frag) {
      root.hidden = true;
      return;
    }

    const byId = new Map();
    frag.querySelectorAll('[data-product-id]').forEach((el) => {
      byId.set(String(el.getAttribute('data-product-id')), el);
    });

    const ordered = ids.map((id) => byId.get(String(id))).filter(Boolean);
    if (ordered.length === 0) {
      root.hidden = true;
      return;
    }

    track.innerHTML = '';
    for (const el of ordered) {
      track.appendChild(/** @type {Node} */ (el.cloneNode(true)));
    }

    this.#bindArrows(root, track);
  }

  /**
   * @param {Element} root
   * @param {HTMLElement} track
   */
  #bindArrows(root, track) {
    const next = root.querySelector('[data-km-bestsellers-next]');
    const prev = root.querySelector('[data-km-bestsellers-prev]');
    if (!(next instanceof HTMLButtonElement) || !(prev instanceof HTMLButtonElement)) return;

    const slideWidth = () => {
      const slide = track.querySelector('[data-km-bestsellers-slide]');
      const gap = parseFloat(getComputedStyle(track).gap || '0');
      return slide ? slide.offsetWidth + gap : track.clientWidth * 0.85;
    };

    const updateDisabled = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max;
    };

    next.addEventListener('click', () => {
      track.scrollBy({ left: slideWidth(), behavior: 'smooth' });
    });
    prev.addEventListener('click', () => {
      track.scrollBy({ left: -slideWidth(), behavior: 'smooth' });
    });
    track.addEventListener('scroll', () => {
      window.requestAnimationFrame(updateDisabled);
    });
    updateDisabled();
    window.addEventListener('resize', updateDisabled);
  }
}

if (!customElements.get('km-recently-viewed-loader')) {
  customElements.define('km-recently-viewed-loader', KmRecentlyViewedLoader);
}
